import { MedplumClient, parseReference } from "@medplum/core";
import type { Bundle, Observation, QuestionnaireResponse, Reference } from "@medplum/fhirtypes";

import { type LaborStage, LaborStages } from "@/encounters/components/EncounterLabor/types/labor";
import {
  getLaborFlowsheetObservationDisplayName,
  isLaborFlowsheetMemberObservation,
  resolveLaborStageMemberObservationFromReference,
} from "@/encounters/components/EncounterLabor/utils/labor-stage-version-diff";
import type { LaborAmendStageMembershipChangeRecord } from "@/encounters/types/labor-amend-provenance";
import { getObservationEncounterAmendParentVersionId } from "@/encounters/utils/questionnaire-response-amend-stamp";
import { getQuestionnaireResponseChangeInstant } from "@/encounters/utils/questionnaire-response-instant";
import { LOINCCodes } from "@/fhir/enums/loinc";
import { collectResourcesFromPaginatedBundle } from "@/utils/pagination";

/**
 * Returns the LOINC answer code for a labor stage observation row.
 */
export function getLaborStageLoincAnswerCode(stageObservation: Observation): string | undefined {
  return stageObservation.valueCodeableConcept?.coding?.[0]?.code;
}

function isLaborStageParentObservation(observation: Observation): boolean {
  return observation.code?.coding?.[0]?.code === LOINCCodes.LaborStage;
}

/**
 * Collects resolved flowsheet member observation ids referenced from a stage `hasMember` list.
 */
export function collectLaborStageMemberObservationIdsFromHasMember(
  hasMember: ReadonlyArray<Reference> | undefined,
  allObservations: ReadonlyArray<Observation>,
): Set<string> {
  const memberObservationIds = new Set<string>();

  for (const memberReference of hasMember ?? []) {
    const resolvedObservation = resolveLaborStageMemberObservationFromReference(
      memberReference,
      allObservations,
    );
    if (resolvedObservation?.id) {
      memberObservationIds.add(resolvedObservation.id);
      continue;
    }

    const [, referenceId] = parseReference(memberReference);
    if (referenceId) {
      memberObservationIds.add(referenceId);
    }
  }

  return memberObservationIds;
}

/**
 * Reads full paginated Observation version history for a labor stage row.
 */
export async function readLaborStageObservationHistory(
  medplum: MedplumClient,
  stageObservationId: string,
): Promise<Observation[]> {
  const initialBundle: Bundle<Observation> = await medplum.readHistory(
    "Observation",
    stageObservationId,
  );

  return collectResourcesFromPaginatedBundle(medplum, initialBundle, "Observation");
}

export type LaborStageObservationHistoryCache = Map<string, Observation[]>;

/**
 * Reads stage observation history with optional per-request caching by stage id.
 */
export async function readLaborStageObservationHistoryCached(
  medplum: MedplumClient,
  stageObservationId: string,
  cache?: LaborStageObservationHistoryCache,
): Promise<Observation[]> {
  const cachedHistory = cache?.get(stageObservationId);
  if (cachedHistory) {
    return cachedHistory;
  }

  const history = await readLaborStageObservationHistory(medplum, stageObservationId);
  cache?.set(stageObservationId, history);
  return history;
}

/**
 * Selects the stage `hasMember` list from pre-fetched history at or before the given instant.
 */
export function selectLaborStageHasMemberFromHistoryVersions(
  versions: ReadonlyArray<Observation>,
  instant: number,
): ReadonlyArray<Reference> {
  let bestMatch: Observation | undefined;
  let bestInstant = Number.NEGATIVE_INFINITY;

  for (const version of versions) {
    const versionInstant = Date.parse(version.meta?.lastUpdated ?? "");
    if (!Number.isFinite(versionInstant) || versionInstant > instant) {
      continue;
    }
    if (versionInstant >= bestInstant) {
      bestInstant = versionInstant;
      bestMatch = version;
    }
  }

  return bestMatch?.hasMember ?? [];
}

/**
 * Returns the stage `hasMember` list as it existed at or before the given instant.
 * When no historical version qualifies, returns an empty list (not the live membership).
 */
export async function getLaborStageHasMemberAtInstant(
  medplum: MedplumClient,
  stageObservation: Observation,
  instant: number,
  cache?: LaborStageObservationHistoryCache,
): Promise<ReadonlyArray<Reference>> {
  if (!stageObservation.id) {
    return [];
  }

  try {
    const versions = await readLaborStageObservationHistoryCached(
      medplum,
      stageObservation.id,
      cache,
    );
    return selectLaborStageHasMemberFromHistoryVersions(versions, instant);
  } catch {
    return [];
  }
}

async function buildMemberObservationIdToStageCodeMapAtInstant(
  medplum: MedplumClient,
  laborStageObservations: ReadonlyArray<Observation>,
  allObservations: ReadonlyArray<Observation>,
  instant: number | "current",
  cache?: LaborStageObservationHistoryCache,
): Promise<Map<string, string>> {
  const memberToStageCode = new Map<string, string>();

  for (const stageObservation of laborStageObservations) {
    if (!isLaborStageParentObservation(stageObservation)) {
      continue;
    }

    const stageCode = getLaborStageLoincAnswerCode(stageObservation);
    if (!stageCode) {
      continue;
    }

    const hasMember =
      instant === "current"
        ? stageObservation.hasMember
        : await getLaborStageHasMemberAtInstant(medplum, stageObservation, instant, cache);

    for (const memberObservationId of collectLaborStageMemberObservationIdsFromHasMember(
      hasMember,
      allObservations,
    )) {
      memberToStageCode.set(memberObservationId, stageCode);
    }
  }

  return memberToStageCode;
}

interface StageHasMemberDelta {
  memberObservationId: string;
  stageCode: string;
  change: "added" | "removed";
}

function mergeLaborStageMembershipChanges(
  changes: LaborAmendStageMembershipChangeRecord[],
): LaborAmendStageMembershipChangeRecord[] {
  const mergedByMemberId = new Map<string, LaborAmendStageMembershipChangeRecord>();

  for (const change of changes) {
    const existingChange = mergedByMemberId.get(change.memberObservationId);
    if (!existingChange) {
      mergedByMemberId.set(change.memberObservationId, change);
      continue;
    }

    mergedByMemberId.set(change.memberObservationId, {
      ...existingChange,
      ...change,
      fromStageLoincCode: change.fromStageLoincCode ?? existingChange.fromStageLoincCode,
      fromStageDisplay: change.fromStageDisplay ?? existingChange.fromStageDisplay,
      toStageLoincCode: change.toStageLoincCode ?? existingChange.toStageLoincCode,
      toStageDisplay: change.toStageDisplay ?? existingChange.toStageDisplay,
      memberDisplayTitle: change.memberDisplayTitle ?? existingChange.memberDisplayTitle,
    });
  }

  return [...mergedByMemberId.values()];
}

function buildLaborStageMembershipChangeRecord(
  memberObservationId: string,
  fromStageLoincCode: string | undefined,
  toStageLoincCode: string | undefined,
  laborStageObservations: ReadonlyArray<Observation>,
): LaborAmendStageMembershipChangeRecord | undefined {
  if (!toStageLoincCode || fromStageLoincCode === toStageLoincCode) {
    return undefined;
  }

  const memberObservation = laborStageObservations.find(
    (observation) => observation.id === memberObservationId,
  );
  if (memberObservation && !isLaborFlowsheetMemberObservation(memberObservation)) {
    return undefined;
  }

  return {
    memberObservationId,
    memberDisplayTitle: getLaborFlowsheetObservationDisplayName(
      memberObservation?.code?.coding?.[0]?.code,
    ),
    fromStageLoincCode,
    toStageLoincCode,
    fromStageDisplay: fromStageLoincCode
      ? LaborStages[fromStageLoincCode as LaborStage]
      : undefined,
    toStageDisplay: LaborStages[toStageLoincCode as LaborStage],
  };
}

/**
 * Collects hasMember add/remove events on labor stage observations after a window start instant.
 */
export async function collectLaborStageHasMemberDeltasAfterInstant(
  medplum: MedplumClient,
  stageObservations: ReadonlyArray<Observation>,
  allObservations: ReadonlyArray<Observation>,
  windowStartInstant: number,
  windowEndInstant: number = Number.POSITIVE_INFINITY,
  cache?: LaborStageObservationHistoryCache,
): Promise<StageHasMemberDelta[]> {
  const deltas: StageHasMemberDelta[] = [];

  for (const stageObservation of stageObservations) {
    if (!stageObservation.id) {
      continue;
    }

    const stageCode = getLaborStageLoincAnswerCode(stageObservation);
    if (!stageCode) {
      continue;
    }

    let versions: Observation[];
    try {
      versions = await readLaborStageObservationHistoryCached(medplum, stageObservation.id, cache);
    } catch {
      continue;
    }

    const sortedVersions = [...versions].sort((firstVersion, secondVersion) => {
      const firstInstant = Date.parse(firstVersion.meta?.lastUpdated ?? "");
      const secondInstant = Date.parse(secondVersion.meta?.lastUpdated ?? "");
      return firstInstant - secondInstant;
    });

    let previousHasMember = selectLaborStageHasMemberFromHistoryVersions(
      versions,
      windowStartInstant,
    );

    for (const version of sortedVersions) {
      const versionInstant = Date.parse(version.meta?.lastUpdated ?? "");
      if (!Number.isFinite(versionInstant) || versionInstant <= windowStartInstant) {
        previousHasMember = version.hasMember ?? [];
        continue;
      }
      if (versionInstant > windowEndInstant) {
        break;
      }

      const currentHasMember = version.hasMember ?? [];
      const previousMemberIds = collectLaborStageMemberObservationIdsFromHasMember(
        previousHasMember,
        allObservations,
      );
      const currentMemberIds = collectLaborStageMemberObservationIdsFromHasMember(
        currentHasMember,
        allObservations,
      );

      for (const memberObservationId of previousMemberIds) {
        if (!currentMemberIds.has(memberObservationId)) {
          deltas.push({
            memberObservationId,
            stageCode,
            change: "removed",
          });
        }
      }

      for (const memberObservationId of currentMemberIds) {
        if (!previousMemberIds.has(memberObservationId)) {
          deltas.push({
            memberObservationId,
            stageCode,
            change: "added",
          });
        }
      }

      previousHasMember = currentHasMember;
    }
  }

  return deltas;
}

function aggregateStageHasMemberDeltasToMoves(
  deltas: StageHasMemberDelta[],
  laborStageObservations: ReadonlyArray<Observation>,
): LaborAmendStageMembershipChangeRecord[] {
  const changes: LaborAmendStageMembershipChangeRecord[] = [];
  const memberIds = new Set(deltas.map((delta) => delta.memberObservationId));

  for (const memberObservationId of memberIds) {
    const memberDeltas = deltas.filter(
      (delta) => delta.memberObservationId === memberObservationId,
    );
    const removals = memberDeltas.filter((delta) => delta.change === "removed");
    const additions = memberDeltas.filter((delta) => delta.change === "added");
    const fromStageLoincCode = removals.at(-1)?.stageCode;
    const toStageLoincCode = additions.at(-1)?.stageCode;

    const changeRecord = buildLaborStageMembershipChangeRecord(
      memberObservationId,
      fromStageLoincCode,
      toStageLoincCode,
      laborStageObservations,
    );
    if (changeRecord) {
      changes.push(changeRecord);
    }
  }

  return changes;
}

function inferLaborStageMembershipChangesFromAmendStampedObservations(
  laborStageObservations: ReadonlyArray<Observation>,
  previousMemberStages: Map<string, string>,
  currentMemberStages: Map<string, string>,
  currentEncounterQuestionnaireResponse: QuestionnaireResponse | undefined,
): LaborAmendStageMembershipChangeRecord[] {
  const currentEncounterVersionId = currentEncounterQuestionnaireResponse?.meta?.versionId;
  if (!currentEncounterVersionId) {
    return [];
  }

  const changes: LaborAmendStageMembershipChangeRecord[] = [];

  for (const observation of laborStageObservations) {
    if (!observation.id || !isLaborFlowsheetMemberObservation(observation)) {
      continue;
    }

    if (getObservationEncounterAmendParentVersionId(observation) !== currentEncounterVersionId) {
      continue;
    }

    const changeRecord = buildLaborStageMembershipChangeRecord(
      observation.id,
      previousMemberStages.get(observation.id),
      currentMemberStages.get(observation.id),
      laborStageObservations,
    );
    if (changeRecord) {
      changes.push(changeRecord);
    }
  }

  return changes;
}

function shouldIncludeMemberObservationIdInInferenceScope(
  memberObservationId: string,
  scopedMemberTargetIds: ReadonlySet<string> | undefined,
): boolean {
  return !scopedMemberTargetIds || scopedMemberTargetIds.has(memberObservationId);
}

/**
 * Infers labor stage membership moves by comparing stage `hasMember` before and after an amend.
 *
 * @param medplum - Medplum client for stage observation history reads.
 * @param laborStageObservations - Current labor stage and member observation bundle for the encounter.
 * @param previousEncounterQuestionnaireResponse - Encounter QuestionnaireResponse baseline before the amend.
 * @param currentEncounterQuestionnaireResponse - Encounter QuestionnaireResponse version being viewed.
 * @param provenanceTargetObservationIds - Optional amend targets used to limit inference scope.
 */
export async function inferLaborStageMembershipChangesForVersionDiff(
  medplum: MedplumClient,
  laborStageObservations: ReadonlyArray<Observation>,
  previousEncounterQuestionnaireResponse: QuestionnaireResponse,
  currentEncounterQuestionnaireResponse?: QuestionnaireResponse,
  provenanceTargetObservationIds?: ReadonlySet<string>,
): Promise<LaborAmendStageMembershipChangeRecord[]> {
  const previousInstant = getQuestionnaireResponseChangeInstant(
    previousEncounterQuestionnaireResponse,
  );
  if (previousInstant === undefined) {
    return [];
  }

  const stageObservations = laborStageObservations.filter(isLaborStageParentObservation);
  if (stageObservations.length === 0) {
    return [];
  }

  const flowsheetMemberTargetIds =
    provenanceTargetObservationIds &&
    [...provenanceTargetObservationIds].filter((observationId) => {
      const observation = laborStageObservations.find(
        (candidate) => candidate.id === observationId,
      );
      return observation ? isLaborFlowsheetMemberObservation(observation) : false;
    });

  const scopedMemberTargetIds =
    flowsheetMemberTargetIds && flowsheetMemberTargetIds.length > 0
      ? new Set(flowsheetMemberTargetIds)
      : undefined;

  const stageHistoryCache: LaborStageObservationHistoryCache = new Map();

  const [previousMemberStages, currentMemberStages] = await Promise.all([
    buildMemberObservationIdToStageCodeMapAtInstant(
      medplum,
      stageObservations,
      laborStageObservations,
      previousInstant,
      stageHistoryCache,
    ),
    buildMemberObservationIdToStageCodeMapAtInstant(
      medplum,
      stageObservations,
      laborStageObservations,
      "current",
      stageHistoryCache,
    ),
  ]);

  const windowEndInstant = currentEncounterQuestionnaireResponse
    ? getQuestionnaireResponseChangeInstant(currentEncounterQuestionnaireResponse)
    : undefined;

  const historyDeltas = await collectLaborStageHasMemberDeltasAfterInstant(
    medplum,
    stageObservations,
    laborStageObservations,
    previousInstant,
    windowEndInstant ?? Number.POSITIVE_INFINITY,
    stageHistoryCache,
  );

  const historyChanges = aggregateStageHasMemberDeltasToMoves(
    historyDeltas.filter((delta) =>
      shouldIncludeMemberObservationIdInInferenceScope(
        delta.memberObservationId,
        scopedMemberTargetIds,
      ),
    ),
    laborStageObservations,
  );

  const stampedObservationChanges = inferLaborStageMembershipChangesFromAmendStampedObservations(
    laborStageObservations,
    previousMemberStages,
    currentMemberStages,
    currentEncounterQuestionnaireResponse,
  ).filter((change) =>
    shouldIncludeMemberObservationIdInInferenceScope(
      change.memberObservationId,
      scopedMemberTargetIds,
    ),
  );

  const candidateMemberIds = new Set<string>([
    ...previousMemberStages.keys(),
    ...currentMemberStages.keys(),
    ...historyDeltas.map((delta) => delta.memberObservationId),
    ...stampedObservationChanges.map((change) => change.memberObservationId),
  ]);

  const instantComparisonChanges: LaborAmendStageMembershipChangeRecord[] = [];

  for (const memberObservationId of candidateMemberIds) {
    if (
      !shouldIncludeMemberObservationIdInInferenceScope(memberObservationId, scopedMemberTargetIds)
    ) {
      continue;
    }

    const changeRecord = buildLaborStageMembershipChangeRecord(
      memberObservationId,
      previousMemberStages.get(memberObservationId),
      currentMemberStages.get(memberObservationId),
      laborStageObservations,
    );
    if (changeRecord) {
      instantComparisonChanges.push(changeRecord);
    }
  }

  return mergeLaborStageMembershipChanges([
    ...instantComparisonChanges,
    ...historyChanges,
    ...stampedObservationChanges,
  ]);
}
